/**
 * Booking quote — same location + pricing APIs as NewBooking.jsx (no alternate paths).
 */
import config from '../config';
import { QUOTE_RATE_CLASS_MAX } from '../bookingQuote/config';
import { formatLocalDateTime } from './bookingQuoteBooking';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

function parseDetailError(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && detail.message) return String(detail.message);
  return String(detail);
}

/** POST /resolve-location with address_text — mirrors handleSearchLocation */
export async function searchLocationByText(addressText) {
  const query = (addressText || '').trim();
  if (!query) throw new Error('Please enter a location to search.');

  const res = await fetch(`${config.customerApiBase}/resolve-location`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ address_text: query }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(parseDetailError(data.detail) || 'Failed to resolve location');
  }

  if (data.type === 'similar' && Array.isArray(data.matches) && data.matches.length) {
    return { kind: 'similar', matches: data.matches };
  }

  if (!data.address_id) {
    throw new Error('Location could not be saved — try a more specific address or pick from the map.');
  }

  return {
    kind: 'resolved',
    address: {
      id: data.address_id,
      text: data.resolved_address || query,
      location: data.latitude != null && data.longitude != null
        ? { lat: Number(data.latitude), lng: Number(data.longitude) }
        : null,
    },
  };
}

/** POST /resolve-location with lat/lng — mirrors resolveAndSetAddress */
export async function resolveLocationByCoordinates(lat, lng) {
  const res = await fetch(`${config.customerApiBase}/resolve-location`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ lat, lng }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(parseDetailError(data.detail) || 'Failed to resolve location');
  }

  if (data.type === 'similar' && Array.isArray(data.matches) && data.matches.length) {
    return { kind: 'similar', matches: data.matches };
  }

  if (!data.address_id) {
    throw new Error('Could not resolve map point to an address.');
  }

  return {
    kind: 'resolved',
    address: {
      id: data.address_id,
      text: data.resolved_address || `${lat}, ${lng}`,
      location: { lat: Number(lat), lng: Number(lng) },
    },
  };
}

/** Mirrors applyAddressSuggestion */
export async function applyLocationSuggestion(suggestion) {
  let resolved = suggestion;

  if (!suggestion?.id) {
    const lat = Number(suggestion?.latitude);
    const lng = Number(suggestion?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      throw new Error('Invalid address suggestion');
    }
    const res = await fetch(`${config.customerApiBase}/resolve-location`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ lat, lng }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.address_id) {
      throw new Error(parseDetailError(data.detail) || 'Failed to resolve selected suggestion');
    }
    resolved = {
      id: data.address_id,
      locality: data.resolved_address,
      latitude: data.latitude,
      longitude: data.longitude,
    };
  }

  const text = (resolved.locality || resolved.resolved_address || resolved.text || '').trim();
  const lat = Number(resolved.latitude);
  const lng = Number(resolved.longitude);

  return {
    id: resolved.id,
    text: text || 'Selected location',
    location: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null,
  };
}

export function formatQuotePickupTime(date) {
  const d = date || new Date(Date.now() + 15 * 60 * 1000);
  return formatLocalDateTime(d);
}

export function formatQuoteListPrice(vehicle) {
  return Math.ceil(Number(vehicle?.estimated_price || 0)).toLocaleString('en-IN');
}

/**
 * POST /resolve-vehicle-pricing — same request/response handling as NewBooking.jsx handleGetVehicleOptions.
 * Class cap (1–9) is applied in the quote UI only, not on the API body.
 */
export async function fetchQuoteVehicleOptions({
  pickupAddressId,
  dropAddressIds,
  pickupTime,
  allClassesRates = true,
  vehicleParams = {},
}) {
  const requestBody = {
    pickup_address_id: String(pickupAddressId),
    drop_address_ids: dropAddressIds.map((id) => String(id)),
    pickup_time: pickupTime || formatQuotePickupTime(),
    source: 'ops',
  };

  const vp = vehicleParams || {};
  const hasVehicleParams = !!(vp.vehicle_name || vp.load_weight || vp.load_length || vp.load_width || vp.load_height || vp.body_type);

  if (allClassesRates) {
    requestBody.all_classes_rates = true;
  }

  if (vp.vehicle_name) requestBody.vehicle_name = vp.vehicle_name;
  if (vp.load_weight) requestBody.load_weight = parseFloat(vp.load_weight);
  if (vp.load_length) requestBody.load_length = parseFloat(vp.load_length);
  if (vp.load_width) requestBody.load_width = parseFloat(vp.load_width);
  if (vp.load_height) requestBody.load_height = parseFloat(vp.load_height);
  if (vp.body_type) requestBody.body_type = vp.body_type;

  if (!allClassesRates && !hasVehicleParams) {
    throw new Error('Please specify vehicle requirements, or use All classes (1–9).');
  }

  const response = await fetch(`${config.customerApiBase}/resolve-vehicle-pricing`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(parseDetailError(errorData.detail) || 'Failed to get vehicle options');
  }

  const data = await response.json();
  if (data.status !== 'ok') {
    throw new Error(data.message || data.detail || 'Failed to get vehicle options');
  }

  return {
    vehicle_options: data.vehicle_options || [],
    route: data.route,
    pricing_mode: data.pricing_mode,
  };
}

export async function fetchCustomerAddressRecommendations(customerId) {
  if (!customerId) return [];
  const res = await fetch(
    `${config.customerApiBase}/customers/${customerId}/address-recommendations`,
    { headers: JSON_HEADERS },
  );
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.addresses) ? data.addresses : [];
}

export { QUOTE_MAX_VEHICLE_CLASSES, QUOTE_RATE_CLASS_MAX } from '../bookingQuote/config';

export function recommendedAddressTitle(address) {
  const locality = String(address?.locality || address?.text || '').trim();
  if (!locality) return 'Saved address';
  return locality.split(',')[0].trim();
}

export function recommendedAddressSubtitle(address) {
  const parts = [address?.city, address?.state, address?.pin_code].filter(Boolean);
  return parts.join(' · ');
}

export function recommendedAddressFromSaved(address) {
  const lat = Number(address?.latitude);
  const lng = Number(address?.longitude);
  const displayText = address?.text || [address?.locality, address?.city].filter(Boolean).join(', ');
  return {
    id: address.id,
    text: displayText,
    location: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null,
  };
}
